import { getEntityRef, getRootContainerProps, getRootContainerPropsDep } from "@client/content/lib/helper";
import { type RedirectFromSettingsArgs } from "@client/content/modules/actions";
import { ModuleButton, ModuleSecondaryButton } from "@client/content/modules/Button";
import FormStyleProvider from "@client/content/Providers/FormStyle/container";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { type GenericMap, Status } from "@client/types";
import FormSurvey from "@common/components/FormSurvey/component";
import { type ButtonProps } from "@common/controls/Button/component";
import { mapFormElements } from "@common/controls/Form/DynamicForm/lib";
import { System } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { mapFormFieldWithContextToDynamicField } from "@common/modules/system/lib";
import { Common } from "@common/types";
import * as React from "react";
import api from "../../api";
import { type Form } from "../../types";

export type MapProps = RootContainerProps & {
  apiInfo: ModuleApiInfo;
  detail: Form.FormDetail;
  email: string | null;
  emailFieldId: string;
  endingDefaults: Form.EndingSettings;
  error: string | null;
  status: string;
  endingsValue: Form.EndingsSettingValue | undefined;
};

export type ActionProps = {
  // progress: (pageId: string, data: GenericMap<GenericMap<any>>, nextPageId: string) => void;
  redirect: (settings: RedirectFromSettingsArgs) => void;
};

type Props = ActionProps & MapProps;
export default (props: Props) => {
  const i18n = useI18n();

  const mapFields = React.useCallback(
    (
      fields: Common.System.PageFormField[],
      values: GenericMap<any>,
      getName: (x: Common.System.PageFormField) => string
    ) => {
      return mapFormElements(
        i18n,
        fields.map((x) => {
          const f = mapFormFieldWithContextToDynamicField(x, getName);
          if (x.formFieldId === props.emailFieldId) {
            f.disabledTooltip = i18n.infoEmailDisabled;
            f.isDisabled = !!props.email;
            f.isRequired = true;
            f.value = props.email;
          }
          return f;
        }),
        values
      );
    },
    [props.email, props.emailFieldId]
  );

  const PrimaryButton = React.useMemo(
    () =>
      ({ children, ...buttonProps }: ButtonProps) =>
        (
          <ModuleButton
            {...getRootContainerProps(props)}
            {...buttonProps}
            isDisabled={props.status === Status.Saving || buttonProps.isDisabled}
          />
        ),
    [...getRootContainerPropsDep(props), props.status]
  );

  const SecondaryButton = React.useMemo(
    () => (buttonProps: ButtonProps) =>
      (
        <ModuleSecondaryButton
          {...getRootContainerProps(props)}
          {...buttonProps}
          isDisabled={props.status === Status.Saving || buttonProps.isDisabled}
        />
      ),
    [...getRootContainerPropsDep(props), props.status]
  );

  const proceed = React.useCallback(
    async (
      page: System.FormPage,
      pageData: GenericMap<GenericMap<any>>,
      leavingPageId: string
    ): Promise<System.FormPage | null> => {
      const pr = await api.progress(props.apiInfo, props.detail!.form.formId, leavingPageId, {
        data: pageData,
        nextPageId: page.formPageId
      });
      if (pr.error) throw new Error(pr.error);
      if (page.formPageTypeKey === "Standard") return page;

      const endingSettings = {
        ...props.endingDefaults,
        ...(props.endingsValue ? props.endingsValue[page.formPageId] : {})
      };
      if (endingSettings.behaviourType === "custom") {
        switch (endingSettings.behaviour) {
          case "message": {
            return {
              ...page,
              description: endingSettings.message ?? "Thank you for your answers!"
            };
          }
          case "redirect": {
            props.redirect({
              ...endingSettings,
              setting: endingSettings.redirect
            });
            return null;
          }
        }
      }

      if (page.redirectUrl) {
        props.redirect({
          ...endingSettings,
          setting: {
            to: "url",
            url: page.redirectUrl
          }
        });
        return null;
      }

      return {
        ...page,
        description: page.description ?? "Thank you for your answers!"
      };
    },
    [props.apiInfo, props.detail, props.endingDefaults, props.endingsValue, props.redirect]
  );

  return (
    <FormStyleProvider {...getEntityRef(props)}>
      <FormSurvey
        PrimaryButton={PrimaryButton}
        SecondaryButton={SecondaryButton}
        data={props.detail}
        mapFields={mapFields}
        proceed={proceed}
      />
    </FormStyleProvider>
  );
};
